import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Flame, Sun, X } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  streak_risk: { icon: Flame, accent: "border-amber-300 bg-amber-50/80", chip: "bg-amber-100 text-amber-800 border-amber-200", label: "Streak at risk" },
  daily_start: { icon: Sun, accent: "border-primary-200 bg-primary-50/70", chip: "bg-primary-50 text-primary-700 border-primary-200", label: "Daily focus" },
};

/**
 * Persistent banner on the Home dashboard for performance notifications
 * (streak risk + daily start). Shows the most recent unread item; tapping
 * "Got it" dismisses it server-side. Admin-only while feature is pre-rollout.
 */
export default function PerfNotifBanner() {
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    try {
      const { data } = await api.get("/performance/notifications", { params: { unread_only: true, limit: 5 } });
      const rows = data?.rows || [];
      // Prefer streak_risk (most actionable) over daily_start.
      const risk = rows.find((r) => r.kind === "streak_risk");
      setItem(risk || rows[0] || null);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  // Light poll to pick up new notifications fired via the admin trigger.
  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user, load]);

  const dismiss = async () => {
    if (!item) return;
    setBusy(true);
    try {
      await api.post(`/performance/notifications/${item.id}/dismiss`);
      setItem(null);
      toast.success("Dismissed");
    } catch {
      toast.error("Could not dismiss");
    } finally { setBusy(false); }
  };

  if (!item) return null;
  const meta = KIND_META[item.kind] || KIND_META.daily_start;
  const Icon = meta.icon;

  return (
    <div
      data-testid={`perf-notif-banner-${item.id}`}
      className={`relative rounded-3xl border ${meta.accent} p-4 sm:p-5 mb-6 shadow-soft`}
    >
      <button
        type="button"
        onClick={dismiss}
        disabled={busy}
        aria-label="Dismiss"
        data-testid="perf-notif-banner-dismiss"
        className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full bg-card/70 hover:bg-card border border-border/60 tap"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className={`h-11 w-11 grid place-items-center rounded-2xl border shrink-0 ${meta.chip}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{meta.label}</p>
          <p className="text-base sm:text-lg font-display font-extrabold mt-0.5 leading-tight">{item.title}</p>
          <p className="text-sm text-muted-foreground mt-1 leading-snug">{item.message}</p>
        </div>
      </div>
    </div>
  );
}
